import {cn, Typography} from '@sollapay/ui';
import {Badge, Button, ButtonGroup, CountBadge, Icon} from '@sollapay/ui/components';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ErrorAlertMessage, PersonCell, RetryAlert} from '@/components';
import {getRosterPeople, memberDisplayName, splitRoster} from '@/features/trust-accounts/members';

import {ShareChangeLog} from './ShareChangeLog';
import {ShareCurrentUserLabel} from './ShareCurrentUserLabel';
import {ShareGuestRow} from './ShareGuestRow';
import {ShareGuestsEmptyState} from './ShareGuestsEmptyState';
import {ShareMemberRow} from './ShareMemberRow';
import {SharePeopleInput} from './SharePeopleInput';
import {SharePowerOfAttorneyConsent} from './SharePowerOfAttorneyConsent';
import {ShareRolePicker} from './ShareRolePicker';
import {useShareInviteForm} from '../hooks';
import {getShareProblemKey, mergeShareLog, SHARE_VIEWS} from '../utils';

import type {ShareMemberRowProps} from './ShareMemberRow';
import type {PowerOfAttorneyGrantor} from './SharePowerOfAttorneyConsent';
import type {ShareInviteFormParams} from '../hooks';
import type {ShareChip, ShareView} from '../utils';
import type {
  TrustAccountGuestEventDTO,
  TrustAccountMemberEventDTO,
  TrustAccountMembersDTO
} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

export interface SharePanelContentProps extends ShareInviteFormParams {
  view: ShareView;
  onViewChange: (view: ShareView) => void;
  accountName: string;
  readOnly: boolean;
  currentUserId?: string;
  memberEvents: TrustAccountMemberEventDTO[];
  guestEvents: TrustAccountGuestEventDTO[];
  isLogLoading?: boolean;
  isLogError?: boolean;
  onRetryLog?: () => void;
  onRetryCandidates?: () => void;
  isGuestsError?: boolean;
  onRetryGuests?: () => void;
  memberError?: unknown;
  onMoveMember: ShareMemberRowProps['onMove'];
  onRemoveMember: ShareMemberRowProps['onRemove'];
  onRemoveGuest: (userId: string) => Promise<void>;
  onResendGuest: (userId: string) => Promise<void>;
  onCopyLink: () => void;
  linkCopied?: boolean;
  onClose?: () => void;
}

/** The share panel: add firm people or guests and manage everyone with access, or browse the change log. */
export const SharePanelContent: FC<SharePanelContentProps> = ({
  view,
  onViewChange,
  accountName,
  memberEvents,
  guestEvents,
  isLogLoading = false,
  isLogError = false,
  onRetryLog,
  onCopyLink,
  linkCopied = false,
  onClose,
  ...people
}) => {
  const logEntries = useMemo(
    () => mergeShareLog(memberEvents, guestEvents),
    [memberEvents, guestEvents]
  );

  return (
    <div data-slot="share-panel" className="flex flex-col">
      <SharePanelHeader
        accountName={accountName}
        view={view}
        onViewChange={onViewChange}
        onClose={onClose}
      />
      <div className="flex flex-col gap-3.5 px-5 pt-3.5 pb-1.5">
        {view === 'log' && (
          <ShareChangeLog
            entries={logEntries}
            isLoading={isLogLoading}
            hasError={isLogError}
            onRetry={onRetryLog}
          />
        )}
        {/* Hidden rather than unmounted, so chips typed before opening the log are still there. */}
        <div className={cn('flex flex-col gap-3.5', view !== 'people' && 'hidden')}>
          <SharePeopleView accountName={accountName} {...people} />
        </div>
      </div>
      <SharePanelFooter linkCopied={linkCopied} onCopyLink={onCopyLink} />
    </div>
  );
};

interface SharePanelHeaderProps {
  accountName: string;
  view: ShareView;
  onViewChange: (view: ShareView) => void;
  onClose?: () => void;
}

const SharePanelHeader: FC<SharePanelHeaderProps> = ({
  accountName,
  view,
  onViewChange,
  onClose
}) => {
  const {t} = useTranslation('trustAccounts');

  return (
    <div className="flex flex-col gap-3.5 px-5 pt-4.5">
      <div className="flex items-center justify-between gap-3">
        <Typography as="h2" weight="bold" className="text-lg">
          {t('share.panel.title', {name: accountName})}
        </Typography>
        {onClose && (
          <Button
            variant="tertiary"
            size="icon-sm"
            aria-label={t('share.panel.close')}
            icon={<Icon name="x-close" />}
            onClick={onClose}
          />
        )}
      </div>
      <ButtonGroup
        variant="segmented"
        items={SHARE_VIEWS.map(value => ({value, label: t(`share.views.${value}`)}))}
        value={view}
        onChange={value => onViewChange(value as ShareView)}
      />
    </div>
  );
};

interface SharePanelFooterProps {
  linkCopied: boolean;
  onCopyLink: () => void;
}

const SharePanelFooter: FC<SharePanelFooterProps> = ({linkCopied, onCopyLink}) => {
  const {t} = useTranslation('trustAccounts');

  return (
    <footer className="mt-2.5 flex items-center justify-between gap-3 border-t border-border px-5 py-3.5">
      <Typography size="xs" color="tertiary" className="whitespace-normal">
        {t('share.footer.note')}
      </Typography>
      <Button
        variant="secondary"
        size="sm"
        icon={<Icon name={linkCopied ? 'check-circle' : 'link'} className="size-4" />}
        onClick={onCopyLink}
        className={cn(linkCopied && 'text-indication-success')}
      >
        <span aria-live="polite">
          {t(linkCopied ? 'share.footer.copied' : 'share.footer.copy')}
        </span>
      </Button>
    </footer>
  );
};

type SharePeopleViewProps = ShareInviteFormParams &
  Pick<
    SharePanelContentProps,
    | 'accountName'
    | 'readOnly'
    | 'currentUserId'
    | 'memberError'
    | 'onRetryCandidates'
    | 'isGuestsError'
    | 'onRetryGuests'
    | 'onMoveMember'
    | 'onRemoveMember'
    | 'onRemoveGuest'
    | 'onResendGuest'
  >;

const SharePeopleView: FC<SharePeopleViewProps> = ({
  accountName,
  readOnly,
  currentUserId,
  memberError,
  onRetryCandidates,
  isGuestsError = false,
  onRetryGuests,
  onMoveMember,
  onRemoveMember,
  onRemoveGuest,
  onResendGuest,
  ...formParams
}) => {
  const {t} = useTranslation('trustAccounts');
  const {roster, guests} = formParams;
  const {trustee} = splitRoster(roster);
  const grantor = {trusteeName: trustee ? memberDisplayName(trustee) : '', accountName};

  return (
    <>
      {!readOnly && formParams.candidatesStatus === 'error' && (
        <RetryAlert description={t('members.candidates.error')} onRetry={onRetryCandidates} />
      )}
      {!readOnly && <ShareAddForm grantor={grantor} {...formParams} />}
      {memberError != null && <ErrorAlertMessage error={memberError} />}
      <div className="flex flex-col gap-3">
        <FirmSection
          roster={roster}
          grantor={grantor}
          readOnly={readOnly}
          currentUserId={currentUserId}
          onMoveMember={onMoveMember}
          onRemoveMember={onRemoveMember}
        />
        <GuestsSection
          guests={guests}
          isError={isGuestsError}
          onRetry={onRetryGuests}
          readOnly={readOnly}
          onRemoveGuest={onRemoveGuest}
          onResendGuest={onResendGuest}
        />
      </div>
    </>
  );
};

type FirmSectionProps = Pick<
  SharePanelContentProps,
  'roster' | 'readOnly' | 'currentUserId' | 'onMoveMember' | 'onRemoveMember'
> & {grantor: PowerOfAttorneyGrantor};

const FirmSection: FC<FirmSectionProps> = ({
  roster,
  grantor,
  readOnly,
  currentUserId,
  onMoveMember,
  onRemoveMember
}) => {
  const {t} = useTranslation('trustAccounts');
  const {trustee, teammates} = splitRoster(roster);

  return (
    <PeopleSection
      title={t('share.sections.firm')}
      count={getRosterPeople(roster).length}
      slot="share-firm-people"
    >
      {trustee && <TrusteeRow trustee={trustee} isCurrentUser={trustee.userId === currentUserId} />}
      {teammates.map(member => (
        <ShareMemberRow
          key={member.userId}
          member={member}
          grantor={grantor}
          readOnly={readOnly}
          isCurrentUser={member.userId === currentUserId}
          onMove={onMoveMember}
          onRemove={onRemoveMember}
        />
      ))}
    </PeopleSection>
  );
};

type GuestsSectionProps = Pick<
  SharePanelContentProps,
  'guests' | 'readOnly' | 'onRemoveGuest' | 'onResendGuest'
> & {isError: boolean; onRetry?: () => void};

/** A failed lookup says so, rather than claiming there are no guests. */
const GuestsSection: FC<GuestsSectionProps> = ({
  guests,
  isError,
  onRetry,
  readOnly,
  onRemoveGuest,
  onResendGuest
}) => {
  const {t} = useTranslation('trustAccounts');
  let empty: ReactNode = null;
  if (isError) empty = <RetryAlert description={t('share.guestsError')} onRetry={onRetry} />;
  else if (!guests.length) empty = <ShareGuestsEmptyState readOnly={readOnly} />;

  return (
    <PeopleSection
      title={t('share.sections.guests')}
      count={isError ? undefined : guests.length}
      slot="share-guests"
      empty={empty}
    >
      {guests.map(guest => (
        <ShareGuestRow
          key={guest.userId}
          guest={guest}
          readOnly={readOnly}
          onRemove={onRemoveGuest}
          onResend={onResendGuest}
        />
      ))}
    </PeopleSection>
  );
};

type ShareAddFormProps = ShareInviteFormParams & {grantor: PowerOfAttorneyGrantor};

const ShareAddForm: FC<ShareAddFormProps> = ({grantor, ...params}) => {
  const {
    field,
    roleDetails,
    blocked,
    sendCount,
    hasFailed,
    isSending,
    isAwaitingPowerOfAttorney,
    submit
  } = useShareInviteForm(params);
  const {candidatesStatus = 'success'} = params;

  return (
    <div data-slot="share-add-form" className="flex flex-col gap-2">
      <div className="flex items-start gap-2">
        <SharePeopleInput {...field} />
        {sendCount > 0 && (
          <ShareSendButton
            count={sendCount}
            isBlocked={
              blocked.length > 0 || isAwaitingPowerOfAttorney || candidatesStatus === 'error'
            }
            isBusy={isSending || candidatesStatus === 'pending'}
            onSend={() => void submit()}
          />
        )}
      </div>
      <ShareProblem blocked={blocked} hasFailed={hasFailed} />
      <ShareRoleDetails grantor={grantor} {...roleDetails} />
    </div>
  );
};

interface ShareSendButtonProps {
  count: number;
  isBlocked: boolean;
  isBusy: boolean;
  onSend: () => void;
}

const ShareSendButton: FC<ShareSendButtonProps> = ({count, isBlocked, isBusy, onSend}) => {
  const {t} = useTranslation('trustAccounts');

  return (
    <Button className="min-h-10" disabled={isBlocked} isLoading={isBusy} onClick={onSend}>
      {t(count > 1 ? 'share.add.submitCount' : 'share.add.submit', {count})}
    </Button>
  );
};

type ShareRoleDetailsProps = ReturnType<typeof useShareInviteForm>['roleDetails'] & {
  grantor: PowerOfAttorneyGrantor;
};

/** Firm people get the role picker (and the consent for new operators); guests alone get the guest note. */
const ShareRoleDetails: FC<ShareRoleDetailsProps> = ({
  grantor,
  role,
  isOperatorForced,
  onRoleChange,
  hasFirmChips,
  hasGuestChips,
  powerOfAttorney
}) => {
  if (hasFirmChips) {
    return (
      <ShareRolePicker
        role={role}
        isOperatorForced={isOperatorForced}
        onRoleChange={onRoleChange}
        hasGuests={hasGuestChips}
      >
        {powerOfAttorney && <SharePowerOfAttorneyConsent grantor={grantor} {...powerOfAttorney} />}
      </ShareRolePicker>
    );
  }
  return hasGuestChips ? <GuestRoleNote /> : null;
};

const GuestRoleNote: FC = () => {
  const {t} = useTranslation('trustAccounts');

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background-secondary px-3 py-2.5">
      <Badge variant="neutral" size="sm">
        {t('share.add.guestRole')}
      </Badge>
      <Typography size="xs" color="tertiary" className="flex-1 whitespace-normal">
        {t('share.add.guestRoleHint')}
      </Typography>
    </div>
  );
};

interface ShareProblemProps {
  blocked: ShareChip[];
  hasFailed: boolean;
}

const ShareProblem: FC<ShareProblemProps> = ({blocked, hasFailed}) => {
  const {t} = useTranslation('trustAccounts');
  const [first] = blocked;

  const getMessage = (): string | null => {
    if (blocked.length > 1) return t('share.add.blockedMany', {count: blocked.length});
    if (first?.kind === 'problem') {
      const reason = t(getShareProblemKey(first.problem));
      return t('share.add.blockedOne', {name: first.name ?? first.email, reason});
    }
    return hasFailed ? t('share.add.failed') : null;
  };

  const message = getMessage();
  if (!message) return null;
  return (
    <Typography role="alert" size="sm" className="whitespace-normal text-text-error">
      {message}
    </Typography>
  );
};

interface PeopleSectionProps {
  title: string;
  count?: number;
  slot: string;
  empty?: ReactNode;
  children: ReactNode;
}

/** An empty section shows `empty` instead of the list, so it isn't announced as a list of one. */
const PeopleSection: FC<PeopleSectionProps> = ({title, count, slot, empty, children}) => (
  <section data-slot={slot} className="flex flex-col gap-1">
    <div className="flex items-center gap-2">
      <Typography size="xs" weight="semibold" color="tertiary">
        {title}
      </Typography>
      {count !== undefined && <CountBadge count={count} />}
    </div>
    {empty ?? <ul className="flex flex-col">{children}</ul>}
  </section>
);

interface TrusteeRowProps {
  trustee: NonNullable<TrustAccountMembersDTO['trustee']>;
  isCurrentUser: boolean;
}

const TrusteeRow: FC<TrusteeRowProps> = ({trustee, isCurrentUser}) => {
  const {t} = useTranslation('trustAccounts');

  return (
    <li data-slot="share-trustee-row" className="flex items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        <PersonCell
          name={memberDisplayName(trustee)}
          nameEnd={isCurrentUser && <ShareCurrentUserLabel />}
          subtitle={trustee.email}
          avatarUrl={trustee.picture}
          seed={trustee.userId}
          emptyLabel={trustee.email}
        />
      </div>
      <div
        data-slot="share-trustee-label"
        className="ms-1 flex shrink-0 items-center gap-1.25 px-2"
      >
        <Icon name="shield-01" className="size-3.25 text-brand-accent" aria-hidden="true" />
        <Typography size="sm" color="tertiary">
          {t('members.trusteeBadge')}
        </Typography>
      </div>
    </li>
  );
};
